/* eslint-disable @typescript-eslint/strict-boolean-expressions, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument */
// @ts-nocheck
import * as arrow from 'apache-arrow';
import { readParquet } from 'parquet-wasm';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, ButtonGroup, useTheme } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { Plot } from './Plot';
import {
  TraceLense,
  TraceStrikeBarType,
  GammaHeatmapDatum,
  PriceCandleWithDateTime,
  ProductType,
  RawSpotgammaLevels,
  ExpirySpotDeltaRow,
  ExpirySpotGammaRow,
  TraceGreek,
  OIEntity,
} from 'types';
import { ExpandableContentWrapper, Loader } from '../../components';
import { Center } from '../../components/shared/Center';
import {
  getGreekValueAtLastPrice,
  getHiroRange,
  getYRange,
  parseContourArray,
  shouldShowAxisLabels,
  getContourData,
  getContourColorOptions,
} from '../../util/oi';
import { TraceControls } from './TraceControls';
import { TraceCompactControls } from './TraceCompactControls';
import { Dayjs } from 'dayjs';
import {
  formatAsCompactNumber,
  getDateFormatted,
  getPriceLines,
  getTzOffsetMs,
  hexToRGBA,
  predicateSearch,
  sigHighLow,
  expectedStartEndForSymbol,
  safeMerge,
  splitCandle,
  useGetEquitiesBySyms,
  ET,
  ONE_MIN_MS,
} from '../../util';
import { dayjs } from 'util/shared/date';
import { TRACE_BAR_TITLES, TraceShowChartType } from '../../config/oi';
import { TraceTimeSlider } from './TraceTimeSlider';
import Plotly, { Image } from 'plotly.js';
import { useElementHeight } from '../../hooks/trace/useElementHeight';
import { useStrikeBars } from '../../hooks/trace/useStrikeBars';
import { useTooltip } from '../../hooks/trace/useTooltip';
import { useStats } from '../../hooks/trace/useStats';
import { screenHeightState, timezoneState, todaysOpenArrState } from 'states';
import { useTraceCtx } from '../../components/trace/TraceContext';
import { useQueryClient } from '@tanstack/react-query';
import {
  prefetchUseDefaultServiceGetV2OpenInterestIntradayDelta,
  prefetchUseDefaultServiceGetV2OpenInterestIntradayGamma,
} from 'gen/stream-api/queries/prefetch';
import {
  useDefaultServiceGetDeltaSignal,
  useDefaultServiceGetPrices,
  useDefaultServiceGetV2OpenInterestIntradayDelta,
  useDefaultServiceGetV2OpenInterestIntradayGamma,
  useDefaultServiceGetV2OpenInterestIntradayTimestamps,
} from 'gen/stream-api/queries';
import { MarketActorKind } from 'gen/stream-api/requests/types.gen';
import { CANDLE_IDX } from 'types/impliedVol';
import {
  useFetchParquetContourData,
  useTraceStreaming,
  CANDLE_REFETCH_INTERVAL_MS,
} from 'hooks/trace/useTraceStreaming';
import { useWasmParquetLoaded } from 'hooks/useWasmParquet';

const END_CHART_X_PADDING = 50;

const SLIDER_INTERVAL_SECONDS = 1 * 60;

const CONTOUR_DATA_SETTLE_DELAY_MINUTES = 5;
const CONTOUR_PARQUET_DATA_REFETCH_TIME_MS = 30_000;

// the accumulation window of the candles fetched from the REST API as opposed
// to those of the streaming websocket interface
const CANDLE_REST_API_WINDOW_TIME_MS = 5_000;

const EQUITIES_BY_SYM_REFETCH_TIME_MS = 10 * 60_000;

const PRICE_LINE_SYMBOL = 'SPX';

type TraceProps = {
  productType: ProductType;
};

const marketActorKindFromOiEntity = new Map<OIEntity, MarketActorKind>([
  [OIEntity.CUST, 'cust'],
  [OIEntity.PROCUST, 'procust'],
  [OIEntity.BD, 'bd'],
  [OIEntity.FIRM, 'firm'],
  [OIEntity.MARKET_MAKER, 'mm'],
]);

export const Trace = ({ productType }: TraceProps) => {
  const theme = useTheme();
  const {
    config,
    updateConfig,
    userTraceV2Enabled,
    mode,
    containerWidth,
    containerHeight,
    isMobile,
    isTablet,
    isDesktop,
    intradayDate,
    intradaySym,
    selectedLense,
    selectedGreek,
    heatmapColorSettings,
    fullscreen,
    setFullscreen,
    showChartType: userShowChartType,
    setShowChartType,
    zoomData,
    setZoomData,
  } = useTraceCtx();

  // Mobile / tablet can't fit Heatmap + StrikeBars side-by-side legibly;
  // when the user state is `Both`, render only the Heatmap. Pure rendering
  // concern — the underlying user preference (`userShowChartType`) is not
  // mutated, so resizing back to desktop restores Both.
  const showChartType =
    !isDesktop && userShowChartType === TraceShowChartType.Both
      ? TraceShowChartType.Heatmap
      : userShowChartType;

  const parquetKeys = useMemo(() => [config.mktActor], [config.mktActor]);
  const mktActor: MarketActorKind = marketActorKindFromOiEntity.get(
    config.mktActor ?? OIEntity.MARKET_MAKER,
  );
  const invert = config.inverted;
  const priceBounds = config.priceZoom;
  const tz = useRecoilValue(timezoneState);
  const screenHeight = useRecoilValue(screenHeightState);
  const strikeBarType = config.strikeBarType;
  const todaysOpenArr = useRecoilValue(todaysOpenArrState);
  const showKeyLevels = config.showKeyLevels;
  const showGexZeroDte = config.showGexZeroDte;
  const strikeBarsHistoryEnabled = config.strikeBarsHistoryEnabled;
  const showColorScale = config.showColorScale;
  const showContourLines = config.showContourLines;
  const oiPriceCandleDuration = config.candleDuration;
  const selectedScaleRange = config.scaleRange;
  const hiroSymsMap = config.hiroSyms;
  const filterLinesBasedOnTimeSlider = config.filterLinesBasedOnTimeSlider;
  const showPriceLine = config.showPriceLine;

  const wasmParquetLoaded = useWasmParquetLoaded();

  // Sizing: the TraceProvider root always measures itself via ResizeObserver,
  // so containerWidth / containerHeight are the single source of truth.
  // Earlier versions also read screenWidth / screenHeight from Recoil and
  // mixed in MUI's viewport-based useIsMobile, which caused flashing on
  // mobile: the address bar collapse-and-reveal would change innerHeight on
  // every scroll, which re-rendered Trace, which resized Plotly, which
  // resized the container, which… kept going. Now layout reacts only to
  // the container size, which is stable.
  //
  // Canvas mode makes the plot area a flex:1 child between the toolbar and
  // slider, so we measure that box and hand its height to Plotly — flexbox
  // computes the leftover space with no hand-tuned chrome constants.
  const plotAreaRef = useRef<HTMLDivElement>(null);
  const plotAreaHeight = useElementHeight(plotAreaRef);

  const SIDE_PADDING_PX = isMobile ? 8 : 16;
  // First-paint fallback only: a rough chrome reservation used until the flex
  // plot area has actually been measured.
  const TOOLBAR_PX = isMobile ? 100 : isTablet ? 90 : 120;
  const TIME_SLIDER_PX = isMobile ? 60 : 70;

  // Smallest chart we'll ever render; below this a scrollbar is preferable to
  // an unusably squashed chart.
  const MIN_PLOT_HEIGHT_PX = 240;

  const width = Math.max(containerWidth - SIDE_PADDING_PX, 0);

  // Canvas reads the measured flex plot area; standalone keeps the legacy
  // viewport-anchored formula (its wrapper is unsized, so the container would
  // otherwise grow with the chart and feed back).
  let height: number;
  if (mode === 'canvas') {
    // Fall back to a chrome estimate pre-measure; clamp to containerHeight so
    // the plot can never overflow even if the flex chain breaks.
    const measured =
      plotAreaHeight || containerHeight - TOOLBAR_PX - TIME_SLIDER_PX;
    height = Math.max(Math.min(measured, containerHeight), MIN_PLOT_HEIGHT_PX);
  } else {
    const supportBtnAllowance = fullscreen ? 35 : 70;
    height = Math.max(
      isMobile
        ? screenHeight - (fullscreen ? 240 : 380) - supportBtnAllowance
        : screenHeight * 0.95 -
            172 -
            (fullscreen ? 0 : 50) -
            supportBtnAllowance,
      240,
    );
  }

  const selectedDateIsToday = intradayDate?.diff(dayjs(), 'days') < 1;

  const candleRefetchInterval = useMemo(() => {
    return selectedDateIsToday ? CANDLE_REFETCH_INTERVAL_MS : undefined;
  }, [selectedDateIsToday]);

  const contourDataRefetchInterval = useMemo(() => {
    return selectedDateIsToday
      ? CONTOUR_PARQUET_DATA_REFETCH_TIME_MS
      : undefined;
  }, [selectedDateIsToday]);

  // Plotly's heatmap takes raw `Date` objects in for the x-axis. In order to
  // display correctly, we need to shift the the raw timestamps by the offset
  // of the browser's timezone to the preferred timezone.
  const tzOffset = useMemo(() => getTzOffsetMs(tz), [tz]);

  const hiroSym = hiroSymsMap?.[intradaySym] ?? intradaySym;

  const { start, end } = expectedStartEndForSymbol(
    hiroSym || PRICE_LINE_SYMBOL,
    intradayDate,
    true,
    false,
    productType,
  );
  const endDay = end.tz(ET).format('YYYY-MM-DD');

  const {
    timestamp2StreamedDeltaCandle,
    timestamp2StreamedPrice,
    rerenderIndicator,
  } = useTraceStreaming({
    enabled: selectedDateIsToday,
    intradayDate,
    hiroSym,
    selectedLense,
  });

  const {
    data: deltaTable,
    isLoading: countourDataLoading,
    error: contourDataError,
  } = useFetchParquetContourData(
    {
      greek: selectedGreek,
      intradayDate,
      intradaySym,
    },
    undefined, // queryKey?: TQueryKey,
    {
      refetchInterval: contourDataRefetchInterval,
      enabled: !userTraceV2Enabled,
    },
  );

  const {
    data: gammaTable,
    error: gammaTableError,
    isLoading: gammaTableLoading,
  } = useFetchParquetContourData(
    {
      greek: TraceLense.GAMMA,
      intradayDate,
      intradaySym,
    },
    undefined, // queryKey?: TQueryKey,
    {
      refetchInterval: contourDataRefetchInterval,
      enabled: !userTraceV2Enabled,
    },
  );

  const {
    data: candlesApiPriceArrayBuffer,
    isLoading: candlesApiPriceArrayBufferLoading,
    error: candlesApiError,
  } = useDefaultServiceGetPrices(
    {
      symbol: PRICE_LINE_SYMBOL,
      timeGte: start.tz(ET).format(),
      timeLt: end.tz(ET).format(),
      limit: 100_000,
      candleDurationMs: CANDLE_REST_API_WINDOW_TIME_MS,
    },
    undefined, // queryKey?: TQueryKey,
    {
      refetchInterval: candleRefetchInterval,
      select: (v: ArrayBuffer) => v,
    },
  );

  const {
    data: deltaLineArrayBuffer,
    isLoading: deltaLineLoading,
    error: deltaLineError,
  } = useDefaultServiceGetDeltaSignal(
    {
      isFiltered: true,
      optionAccumulationType: 'Total',
      optionOrigin: 'All',
      symbol: hiroSym,
      timeGte: start.tz(ET).format(),
      timeLt: end.tz(ET).format(),
      limit: 100_000,
      candleDurationMs: CANDLE_REST_API_WINDOW_TIME_MS,
    },
    undefined, // queryKey?: TQueryKey,
    {
      refetchInterval: candleRefetchInterval,
    },
  );

  const {
    data: intradayTimestampsRaw,
    isLoading: intradayTimestampsLoading,
    error: intradayTimestampsError,
  } = useDefaultServiceGetV2OpenInterestIntradayTimestamps(
    {
      symbol: 'SPX',
      date: endDay,
      greek: selectedGreek,
      mktActor,
    },
    undefined, // queryKey?: TQueryKey,
    {
      refetchInterval: contourDataRefetchInterval,
      enabled: userTraceV2Enabled,
    },
  );
  const intradayTimestamps: Dayjs[] = useMemo(
    () =>
      intradayTimestampsRaw != null
        ? (intradayTimestampsRaw as string[]).map((s: string) => dayjs(s))
        : null,
    [intradayTimestampsRaw],
  );

  const deltaLineBucketed = useMemo(
    () => {
      if (!deltaLineArrayBuffer) {
        return;
      }
      const candles = deltaLineArrayBuffer?.candles;
      if (!candles?.length) {
        return undefined;
      }
      let streamedCandles: Array<number[]> = [];
      if (selectedDateIsToday) {
        const lastCandleTimestamp =
          candles[candles.length - 1][CANDLE_IDX.TIMESTAMP_OPEN];
        const oldestStreamingCandleTimestamp =
          lastCandleTimestamp + CANDLE_REST_API_WINDOW_TIME_MS;
        streamedCandles = Array.from(timestamp2StreamedDeltaCandle.keys())
          .filter((ts: number): boolean => ts >= oldestStreamingCandleTimestamp)
          .sort((a, b) => a - b)
          .map((ts) => timestamp2StreamedDeltaCandle.get(ts));
      }

      let currentCandle = null;
      const result = [];
      for (const candleTuple of [...candles, ...streamedCandles]) {
        const bucketTime =
          Math.floor(
            candleTuple[CANDLE_IDX.TIMESTAMP_OPEN] /
              1_000 /
              oiPriceCandleDuration,
          ) *
          oiPriceCandleDuration *
          1_000;
        if (
          !currentCandle ||
          currentCandle[CANDLE_IDX.TIMESTAMP_OPEN] < bucketTime
        ) {
          currentCandle = candleTuple.slice();
          currentCandle[CANDLE_IDX.TIMESTAMP_OPEN] = bucketTime;
          result.push(currentCandle);
        } else {
          currentCandle[CANDLE_IDX.HIGH] = Math.max(
            candleTuple[CANDLE_IDX.HIGH],
            currentCandle[CANDLE_IDX.HIGH],
          );
          currentCandle[CANDLE_IDX.LOW] = Math.min(
            candleTuple[CANDLE_IDX.LOW],
            currentCandle[CANDLE_IDX.LOW],
          );
          currentCandle[CANDLE_IDX.CLOSE] += candleTuple[CANDLE_IDX.CLOSE];
        }
      }
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      deltaLineArrayBuffer,
      oiPriceCandleDuration,
      selectedDateIsToday,
      timestamp2StreamedDeltaCandle,
      rerenderIndicator,
    ],
  );

  const timestampParam = config.timestamp;

  const setTimestamp = useCallback(
    (newVal?: dayjs.Dayjs | null) => {
      updateConfig({ timestamp: newVal?.format() });
    },
    [updateConfig],
  );

  // TODO; Deduplicate with `timestamp`.
  // This breaks a dependency cycle that would exist between timestamp and
  // intradayDeltaParquet and intradayGammaParquet fetches.
  const intradayTimestamp: Dayjs | undefined = useMemo<
    Dayjs | undefined
  >(() => {
    if (timestampParam != null) {
      const ts = dayjs(timestampParam);
      if (
        ts.isValid() &&
        (!(intradayTimestamps?.length > 0) ||
          intradayTimestamps.find((time) => ts.isSame(time)))
      ) {
        return ts;
      }
    }
    return intradayTimestamps?.length > 0
      ? intradayTimestamps[intradayTimestamps.length - 1]
      : undefined;
  }, [intradayTimestamps, timestampParam]);

  const traceTimestamp = intradayTimestamp?.toISOString();
  const {
    data: intradayDeltaParquet,
    isLoading: intradayDeltaLoading,
    error: intradayDeltaError,
  } = useDefaultServiceGetV2OpenInterestIntradayDelta(
    {
      symbol: 'SPX',
      date: endDay,
      mktActor,
      ts: traceTimestamp,
    },
    undefined,
    {
      refetchInterval: contourDataRefetchInterval,
      select: (v: ArrayBuffer) => v,
      enabled: selectedGreek === TraceGreek.Delta && userTraceV2Enabled,
    },
  );

  const {
    data: mmGammaParquet,
    isLoading: _mmGammaLoading, // Don't hold off rendering just for the vol gauge
    error: mmGammaError,
  } = useDefaultServiceGetV2OpenInterestIntradayGamma(
    {
      symbol: 'SPX',
      date: endDay,
      mktActor: 'mm',
      ts: intradayTimestamp?.toISOString(),
    },
    undefined,
    {
      refetchInterval: contourDataRefetchInterval,
      select: (v: ArrayBuffer) => v,
      enabled: userTraceV2Enabled,
    },
  );

  const {
    data: intradayGammaParquet,
    isLoading: intradayGammaLoading,
    error: intradayGammaError,
  } = useDefaultServiceGetV2OpenInterestIntradayGamma(
    {
      symbol: 'SPX',
      date: endDay,
      mktActor,
      ts: intradayTimestamp?.toISOString(),
    },
    undefined, // queryKey?: TQueryKey,
    {
      refetchInterval: contourDataRefetchInterval,
      select: (v: ArrayBuffer) => v,
      enabled: userTraceV2Enabled,
    },
  );

  const expiryAndSpotToDeltaTable = useMemo(() => {
    if (!intradayDeltaParquet || !wasmParquetLoaded) {
      return undefined;
    }
    return arrow.tableFromIPC<ExpirySpotDeltaRow>(
      readParquet(new Uint8Array(intradayDeltaParquet)).intoIPCStream(),
    );
  }, [intradayDeltaParquet, wasmParquetLoaded]);

  const expiryAndSpotToGammaTable = useMemo(() => {
    if (!intradayGammaParquet || !wasmParquetLoaded) {
      return;
    }
    return arrow.tableFromIPC<ExpirySpotGammaRow>(
      readParquet(new Uint8Array(intradayGammaParquet)).intoIPCStream(),
    );
  }, [intradayGammaParquet, wasmParquetLoaded]);

  const mmGammaTable = useMemo(() => {
    if (!mmGammaParquet || !wasmParquetLoaded) {
      return;
    }
    return arrow.tableFromIPC<ExpirySpotGammaRow>(
      readParquet(new Uint8Array(mmGammaParquet)).intoIPCStream(),
    );
  }, [mmGammaParquet, wasmParquetLoaded]);

  const expiryAndSpotToGreekTable = useMemo(() => {
    return userTraceV2Enabled
      ? selectedGreek === TraceGreek.Delta
        ? expiryAndSpotToDeltaTable
        : expiryAndSpotToGammaTable
      : selectedGreek === TraceGreek.Delta
        ? deltaTable
        : gammaTable;
  }, [
    userTraceV2Enabled,
    selectedGreek,
    expiryAndSpotToDeltaTable,
    expiryAndSpotToGammaTable,
    deltaTable,
    gammaTable,
  ]);

  const timestamps: Dayjs[] = useMemo<Dayjs[]>(() => {
    if (userTraceV2Enabled) {
      return intradayTimestamps ?? [];
    } else if (!expiryAndSpotToGreekTable) {
      return [];
    }
    const seenTimes: Set<number> = new Set<number>();
    (expiryAndSpotToGreekTable.toArray() as GammaHeatmapDatum[]).forEach(
      (d) => {
        const bucketTime =
          Math.floor(+d.timestamp / 1_000 / SLIDER_INTERVAL_SECONDS) *
          SLIDER_INTERVAL_SECONDS *
          1_000;
        seenTimes.add(bucketTime);
      },
    );

    const contourSettleTime = dayjs()
      .subtract(CONTOUR_DATA_SETTLE_DELAY_MINUTES, 'minutes')
      .valueOf();

    return Array.from(seenTimes.values())
      .sort((a, b) => a - b)
      .filter((t) => t <= contourSettleTime)
      .map((t) => dayjs(t));
  }, [intradayTimestamps, userTraceV2Enabled, expiryAndSpotToGreekTable]);

  // TODO: Deduplicate with intradayTimestamp when fully on v2
  const [timestamp, isLastTimestamp]: [Dayjs, boolean] = useMemo<
    [Dayjs, boolean]
  >(() => {
    if (timestampParam != null) {
      const ts = dayjs(timestampParam);

      if (
        ts.isValid() &&
        (timestamps.length === 0 || timestamps.find((time) => ts.isSame(time)))
      ) {
        const isLastTimestamp = ts.isSame(timestamps[timestamps.length - 1]);
        if (isLastTimestamp) {
          setTimestamp(undefined);
        }
        return [ts, isLastTimestamp];
      }
    }
    const result = timestamps?.[timestamps.length - 1];
    if (result) {
      const currentParamTs = dayjs(timestampParam);
      if (
        currentParamTs &&
        currentParamTs.isValid() &&
        result != currentParamTs &&
        timestampParam
      ) {
        setTimestamp(undefined);
      }
    }
    return [result, true];
    // Adding `setTimestamps` as a dependency causes infinte renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timestamps, timestampParam]);

  const isLatestTimestamp =
    timestamps?.length > 0 &&
    timestamp != null &&
    timestamps[timestamps.length - 1] == timestamp;

  const { priceCandles, boundsStr } = useMemo<{
    priceCandles?: PriceCandleWithDateTime[];
    boundsStr?: string;
  }>(
    () => {
      let candles = (
        candlesApiPriceArrayBuffer?.candles as Array<Array<number>> | undefined
      )?.map((c) => {
        const [time, open, high, low, close] = splitCandle(c);
        return {
          time,
          datetime: dayjs(time),
          open,
          high,
          low,
          close,
        };
      });
      if (!candles?.length) {
        return {};
      }
      let streamedCandles: PriceCandleWithDateTime[] = [];
      if (selectedDateIsToday) {
        const lastCandleTimestamp = candles[candles.length - 1].time;
        const oldestStreamingCandleTimestamp =
          lastCandleTimestamp + CANDLE_REST_API_WINDOW_TIME_MS;
        streamedCandles = Array.from(timestamp2StreamedPrice.keys())
          .filter((ts) => ts >= oldestStreamingCandleTimestamp)
          .sort((a, b) => a - b)
          .map((ts) => {
            const [_, open, high, low, close] = timestamp2StreamedPrice.get(
              ts,
            ) as number[];
            return {
              time: ts,
              datetime: dayjs(ts),
              open,
              high,
              low,
              close,
            };
          });
      }
      candles = [...candles, ...streamedCandles];

      const lo = candles.reduce((lo, c) => Math.min(lo, c.low), Infinity);
      const hi = candles.reduce((hi, c) => Math.max(hi, c.high), -Infinity);
      const boundsStr = `${lo * (1 - priceBounds)}, ${hi * (1 + priceBounds)}`;
      return { priceCandles: candles, boundsStr };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      candlesApiPriceArrayBuffer,
      priceBounds,
      timestamp2StreamedPrice,
      selectedDateIsToday,
      rerenderIndicator,
    ],
  );

  const getLastCandle = useCallback((): PriceCandleWithDateTime | undefined => {
    if (!priceCandles) {
      return;
    }
    let idx = priceCandles.length - 1;
    if (timestamp != null && !isLatestTimestamp) {
      const tgt = timestamp.valueOf();
      idx = predicateSearch(priceCandles, (c) => c.time <= tgt);
    }
    return priceCandles[idx];
  }, [priceCandles, timestamp, isLatestTimestamp]);

  const contourData = useMemo(() => {
    if (!expiryAndSpotToGreekTable) {
      return;
    }
    const data = getContourData(
      expiryAndSpotToGreekTable,
      timestamp?.valueOf(),
      userTraceV2Enabled ? [selectedGreek] : parquetKeys,
      invert,
      boundsStr?.split(',')?.map((v: string): number => parseFloat(v)) ?? null,
      tzOffset,
      selectedLense,
      priceCandles,
      width,
      userTraceV2Enabled,
    );

    const colorContourOptions = getContourColorOptions(
      data?.chartData?.z,
      heatmapColorSettings,
      selectedLense,
      selectedScaleRange,
      userTraceV2Enabled,
    );
    return {
      ...data,
      chartData: {
        ...data?.chartData,
        ...colorContourOptions,
      },
    };
  }, [
    expiryAndSpotToGreekTable,
    tzOffset,
    selectedLense,
    selectedGreek,
    boundsStr,
    priceCandles,
    heatmapColorSettings,
    invert,
    selectedScaleRange,
    timestamp,
    userTraceV2Enabled,
    parquetKeys,
    width,
  ]);

  const minX = contourData?.firstChartTimestamp;
  const maxX = contourData?.lastChartTimestamp;

  const [lastMinY, setLastMinY] = useState(null);
  const [lastMaxY, setLastMaxY] = useState(null);

  const [minY, maxY]: [undefined | number, undefined | number] = useMemo<
    [undefined | number, undefined | number]
  >(() => {
    const contourStrikes = contourData?.chartData?.y ?? [];
    const min = contourStrikes[0];
    const max = contourStrikes[contourStrikes.length - 1];
    return [min, max];
  }, [contourData]);

  useEffect(() => {
    if (minY != null) {
      setLastMinY(minY);
    }
    if (maxY != null) {
      setLastMaxY(maxY);
    }
  }, [minY, maxY]);

  const deltaLine = useMemo(() => {
    if (!deltaLineBucketed || (deltaLineBucketed?.length ?? 0) <= 0 || !minX) {
      return undefined;
    }
    const color =
      heatmapColorSettings?.hiroColor ??
      theme?.palette?.trace?.heatmapSettings?.hiroColor;
    const maxTs =
      isLastTimestamp || !filterLinesBasedOnTimeSlider
        ? (maxX ?? 0)
        : timestamp?.valueOf();
    let deltaSumAccum = 0;
    const candles = deltaLineBucketed
      .map((candleTuple) => {
        deltaSumAccum += candleTuple[CANDLE_IDX.CLOSE];
        return {
          timestamp: candleTuple[CANDLE_IDX.TIMESTAMP_OPEN],
          closeAccum: deltaSumAccum,
        };
      })
      .filter((c) => c.timestamp >= minX && c.timestamp <= maxTs);
    const y = candles.map((c) => c.closeAccum);
    if (!y.length) {
      return undefined;
    }
    const min = y.reduce((accum, val) => Math.min(accum, val));
    const max = y.reduce((accum, val) => Math.max(accum, val));

    return {
      hoverinfo: 'skip',
      mode: 'lines',
      name: 'hiro',
      type: 'scatter',
      xaxis: 'x',
      yaxis: 'y2',
      candles,
      x: candles.map((c) =>
        dayjs(c.timestamp + tzOffset)
          .tz(tz)
          .toDate(),
      ),
      y,
      line: {
        width: 2,
        color,
      },
      min,
      max,
    };
  }, [
    deltaLineBucketed,
    heatmapColorSettings,
    maxX,
    minX,
    theme,
    tz,
    tzOffset,
    timestamp,
    isLastTimestamp,
    filterLinesBasedOnTimeSlider,
  ]);

  const {
    setStrikeBarsLoading,
    strikeBarsData,
    strikeBarsTracker,
    strikeBarsRange,
    resetRange: resetStrikeBarsRange,
    strikeBarsLoading,
    showZeroLine,
    triggerUpdate: triggerStrikeBarsUpdate,
  } = useStrikeBars({
    timestamp,
    timestamps,
    minY: minY ?? lastMinY,
    maxY: maxY ?? lastMaxY,
    height,
  });

  const { statsDataMap } = useStats({
    intradaySym,
    intradayDate,
    userTraceV2Enabled,
  });

  const { data: equitiesBySyms } = useGetEquitiesBySyms(
    {
      endDate: intradayDate,
      sym: intradaySym,
    },
    undefined, // queryKey?: TQueryKey,
    {
      refetchInterval: EQUITIES_BY_SYM_REFETCH_TIME_MS,
    },
  );

  const priceCandlesBucketed = useMemo(() => {
    if (!priceCandles) {
      return;
    }
    let currentCandle = null;
    const result = [];
    for (const candle of priceCandles) {
      const bucketTime =
        Math.floor(candle.time / 1_000 / oiPriceCandleDuration) *
        oiPriceCandleDuration *
        1_000;
      if (!currentCandle || currentCandle.time < bucketTime) {
        currentCandle = { ...candle };
        currentCandle.time = bucketTime;
        result.push(currentCandle);
      } else {
        currentCandle.high = Math.max(candle.high, currentCandle.high);
        currentCandle.low = Math.min(candle.low, currentCandle.low);
        currentCandle.close = candle.close;
      }
    }
    return result;
  }, [oiPriceCandleDuration, priceCandles]);

  const priceCandleChartdata = useMemo(() => {
    if (!priceCandlesBucketed || !minX) {
      return;
    }
    const colors = safeMerge(
      theme?.palette?.trace?.heatmapSettings,
      heatmapColorSettings,
    );
    const maxTs =
      isLastTimestamp || !filterLinesBasedOnTimeSlider
        ? (maxX ?? 0)
        : timestamp?.valueOf();
    const candles = priceCandlesBucketed.filter(
      (c) => c.time >= minX && c.time <= maxTs,
    );

    return {
      name: 'price',
      type: 'candlestick',
      xaxis: 'x',
      hoverinfo: 'skip',
      candles,
      x: candles.map((c) =>
        dayjs(c.time + tzOffset)
          .tz(tz)
          .toDate(),
      ),
      open: candles.map((c) => c.open),
      high: candles.map((c) => c.high),
      low: candles.map((c) => c.low),
      close: candles.map((c) => c.close),
      decreasing: {
        fillcolor: colors?.candleDownFillColor,
        line: {
          color: colors?.candleDownLineColor,
        },
      },
      increasing: {
        fillcolor: colors?.candleUpFillColor,
        line: {
          color: colors?.candleUpLineColor,
        },
      },
    };
  }, [
    heatmapColorSettings,
    minX,
    maxX,
    theme,
    timestamp,
    tz,
    tzOffset,
    priceCandlesBucketed,
    isLastTimestamp,
    filterLinesBasedOnTimeSlider,
  ]);

  // Price Action line: continuous close-price trace overlaid on heatmap & strike bars
  const priceActionLineData = useMemo(() => {
    if (!showPriceLine || !priceCandlesBucketed || !minX) {
      return undefined;
    }
    const maxTs =
      isLastTimestamp || !filterLinesBasedOnTimeSlider
        ? (maxX ?? 0)
        : timestamp?.valueOf();
    const candles = priceCandlesBucketed.filter(
      (c) => c.time >= minX && c.time <= maxTs,
    );
    if (!candles.length) {
      return undefined;
    }
    const x = candles.map((c) =>
      dayjs(c.time + tzOffset)
        .tz(tz)
        .toDate(),
    );
    const y = candles.map((c) => c.close);

    return {
      x,
      y,
      mode: 'lines',
      type: 'scatter' as const,
      xaxis: 'x',
      yaxis: 'y',
      line: {
        color: theme.palette.text.secondary,
        width: 1.5,
      },
      name: 'Price Action (PA) Line',
      hoverinfo: 'skip',
      showlegend: false,
    } as Plotly.PlotData;
  }, [
    showPriceLine,
    priceCandlesBucketed,
    minX,
    maxX,
    isLastTimestamp,
    filterLinesBasedOnTimeSlider,
    timestamp,
    tzOffset,
    tz,
    theme,
  ]);

  const volGaugeTableRef = userTraceV2Enabled ? mmGammaTable : gammaTable;

  const gammaAtLastPriceUninverted = useMemo<number | undefined>(() => {
    const lastCandle = getLastCandle();
    const table = volGaugeTableRef?.toArray();
    if (table == null || lastCandle == null || table.length === 0) {
      return undefined;
    }

    const { y, z, rawChartTimes } = parseContourArray(
      table,
      timestamp?.valueOf(),
      userTraceV2Enabled ? [TraceGreek.Gamma] : parquetKeys,
      false, // uninverted
      null, // Process all data. We want no priceBounds.
      tzOffset,
      TraceLense.GAMMA,
      userTraceV2Enabled,
    );

    return getGreekValueAtLastPrice(
      y,
      z,
      rawChartTimes,
      lastCandle,
      TraceLense.GAMMA,
    );
  }, [
    parquetKeys,
    timestamp,
    volGaugeTableRef,
    getLastCandle,
    tzOffset,
    userTraceV2Enabled,
  ]);

  // On mobile the key-level labels (Call Wall, Put Wall, etc.) clutter
  // the tiny chart. Drop the text annotations but keep the lines.
  const suppressLevelLabels = isMobile;
  const levelsChartData = useMemo(() => {
    const contourX = contourData?.chartData?.x ?? [];
    if (
      equitiesBySyms == null ||
      contourData == null ||
      contourX.length === 0 ||
      !showKeyLevels ||
      showChartType === TraceShowChartType.StrikeBars
    ) {
      return { chartData: [], annotations: [] };
    }

    // get the min and max x coordinates for the chart since the lines we draw will need them so they appear
    // as horizontal lines
    const x1 = contourX[0];
    const x2 = contourX[contourX.length - 1];
    // always show the labels at 6 hours past the first coordinate
    // TODO: IMPROVE THIS IN FUTURE AND PLACE IT IN A BETTER LOCATION THAN 6 HOURS AHEAD
    const xMid = Math.max(
      dayjs(x1).add(1, 'hour'),
      Math.min(
        dayjs(x1).add(x2 - x1 - 10 * 60 * 1_000, 'milliseconds'),
        dayjs(x1).add(6, 'hour'),
      ),
    );

    // don't show levels that are not within the strike ranges
    const contourStrikes = contourData.chartData.y;
    const minY = (contourStrikes && contourStrikes[0]) ?? 0;
    const maxY =
      (contourStrikes && contourStrikes[contourStrikes.length - 1]) ?? 0;

    const priceLines = getPriceLines(
      equitiesBySyms as unknown as RawSpotgammaLevels,
      theme,
      sigHighLow(equitiesBySyms, todaysOpenArr, getDateFormatted(intradayDate)),
      true, // get key levels. `showKeyLevels` is guaranteed to be true here
    );
    const chartData: Plotly.Data[] = [];
    const annotations: Plotly.Data[] = [];

    Object.keys(priceLines).forEach((priceLineName) => {
      const levelData = priceLines[priceLineName];
      const val = levelData?.value ?? 0;
      if (val < minY || val > maxY) {
        return;
      }

      chartData.push({
        x: [x1, x2],
        y: [val, val],
        xaxis: 'x',
        yaxis: 'y',
        mode: 'lines',
        line: {
          color: levelData.color,
          width: 1,
        },
        name: priceLineName,
        hoverinfo: 'y',
      });

      // find the leftmost annotation with an overlapping y val
      let neighbor = null;
      for (const annotation of annotations) {
        if (Math.abs(annotation.y - val) <= 10) {
          if (neighbor == null || neighbor.x > annotation.x) {
            neighbor = annotation;
          }
        }
      }

      // subtract one hour from the left overlapping annotation label to ensure no overlap
      const xCoord =
        neighbor != null ? neighbor.x - 60 * 60 * 1_000 : xMid.valueOf();

      annotations.push({
        x: xCoord,
        y: val,
        text: priceLineName,
        font: {
          size: 11,
          color: '#000000',
        },
        showarrow: false,
        bgcolor: hexToRGBA(levelData.color, 0.65),
        borderpad: 4,
      });
    });
    return {
      chartData,
      annotations: suppressLevelLabels ? [] : annotations,
    };
  }, [
    contourData,
    equitiesBySyms,
    todaysOpenArr,
    theme,
    showKeyLevels,
    intradayDate,
    showChartType,
    suppressLevelLabels,
  ]);

  // strike bars are out of date if youre selecting the latest timestamp but the latest timestamp
  // we have for the strike bars is < than that
  const strikeBarsOutOfDate =
    timestamps?.length > 0 &&
    selectedDateIsToday &&
    (timestamp?.valueOf() ?? 0) ===
      timestamps[timestamps.length - 1].valueOf() &&
    (strikeBarsData?.latestTimestamp ?? Infinity) <
      timestamps[timestamps.length - 1].valueOf();

  useEffect(
    () => {
      if (strikeBarsOutOfDate) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        triggerStrikeBarsUpdate();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // If:
      // * strikeBarsOutOfDate AND
      // * we fetch new strike bars and they too happen to be behind the latest timestamp
      // then, we will never refetch strike bars again without the following term.
      //
      // If the above race condition ever happens, we want to trigger another
      // fetch of strike bars on the next update of the timestamps.
      //
      // Hence, the following term:
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timestamps?.[timestamps.length - 1]?.valueOf(),
      strikeBarsOutOfDate,
      userTraceV2Enabled,
    ],
  );

  const gexTitle = () => {
    if (isMobile) {
      return undefined;
    }

    const title = TRACE_BAR_TITLES.get(strikeBarType)!;
    if (
      showGexZeroDte &&
      !strikeBarsHistoryEnabled &&
      (strikeBarsData?.chartData?.length ?? 0) > 0 &&
      strikeBarsData!.chartData[0].x.find((v: number) => v !== 0) == null
    ) {
      return `${title}<br />(market closed, 0DTE n/a)`;
    }

    if (strikeBarsOutOfDate) {
      return `${title}<br />(out of date, updating...)`;
    }

    return title;
  };

  const queryClient = useQueryClient();
  const prefetch = useCallback(
    (tgtEpochMillis: number) => {
      if (!userTraceV2Enabled) {
        return;
      }
      // Prefetch the next 1.5hrs of frames at 5-minute intervals
      const candidates = new Set(timestamps.map((t) => t.valueOf()));
      const maxTime = Math.min(
        timestamps[timestamps.length - 1] ?? -Infinity,
        tgtEpochMillis + 90 * ONE_MIN_MS,
      );
      for (
        let toFetch = tgtEpochMillis;
        toFetch <= maxTime;
        toFetch += 5 * ONE_MIN_MS
      ) {
        if (!candidates.has(toFetch)) {
          continue;
        }
        const opts = {
          symbol: 'SPX',
          date: endDay,
          mktActor,
          ts: dayjs(toFetch).toISOString(),
        };
        if (selectedGreek == TraceGreek.Delta) {
          prefetchUseDefaultServiceGetV2OpenInterestIntradayDelta(
            queryClient,
            opts,
          ).then(
            (_) => {},
            (err) => {
              console.error(err);
            },
          );
        }
        prefetchUseDefaultServiceGetV2OpenInterestIntradayGamma(
          queryClient,
          opts,
        ).then(
          (_) => {},
          (err) => {
            console.error(err);
          },
        );
      }
    },
    [
      endDay,
      mktActor,
      queryClient,
      selectedGreek,
      timestamps,
      userTraceV2Enabled,
    ],
  );

  const onZoom = useCallback(
    (eventData: Readonly<Plotly.PlotRelayoutEvent>) => {
      const xZoomedOut = eventData['xaxis.autorange'];
      const yZoomedOut = eventData['yaxis.autorange'];

      const xBounds = [
        eventData['xaxis.range[0]'],
        eventData['xaxis.range[1]'],
      ].filter((d) => d != null);
      const yBounds = [eventData['yaxis.range[0]'], eventData['yaxis.range[1]']]
        .filter((d) => d != null)
        .map((d) => Math.floor(d));

      const y2Bounds = [
        eventData['yaxis2.range[0]'] as number | undefined,
        eventData['yaxis2.range[1]'] as number | undefined,
      ]
        .filter((d) => d != null)
        .map((d) => Math.floor(d));

      const newZoomData: ZoomData = {};
      // for some reason, plotly sometimes does not pass in autorange for the y2 axis.
      // so we dont always know when y2 is zoomed out. instead, use the autorange for y
      if (!yZoomedOut) {
        newZoomData.y = yBounds.length > 0 ? yBounds : zoomData?.y;
        newZoomData.y2 = y2Bounds.length > 0 ? y2Bounds : zoomData?.y2;
      }
      if (!xZoomedOut) {
        newZoomData.x = xBounds.length > 0 ? xBounds : zoomData?.x;
      }
      const newZoomDataState =
        Object.keys(newZoomData).length > 0 ? newZoomData : undefined;
      setZoomData(newZoomDataState);
    },
    [zoomData, setZoomData],
  );

  // Strike bars render only when showChartType !== Heatmap AND a bar type
  // is selected. When they don't render, give their reserved slice back to
  // the heatmap — otherwise we waste a chunk of chart width to nothing.
  const strikeBarsVisible =
    strikeBarType !== TraceStrikeBarType.NONE &&
    showChartType !== TraceShowChartType.Heatmap;
  const xAxisStartDomain = !strikeBarsVisible
    ? 0.05
    : showChartType === TraceShowChartType.StrikeBars
      ? 0.05
      : isDesktop
        ? 0.27
        : 0.2; // mobile / tablet Both: narrower bars panel
  // Reserve right-edge space for the y2 (HIRO) axis labels and, when
  // visible, the heatmap color-scale colorbar. If we don't reserve enough,
  // Plotly has no room to render them — labels get reflowed to the wrong
  // side and the colorbar is clipped.
  //   - y2 axis labels (e.g. "4B") need ~30px
  //   - color scale colorbar needs ~50px on top of that
  const rightReservationPx = !isDesktop
    ? showColorScale
      ? 70
      : 36
    : END_CHART_X_PADDING;
  const xAxisEndDomain = isMobile
    ? showChartType === TraceShowChartType.StrikeBars
      ? 0.95
      : 1
    : 1 - rightReservationPx / Math.max(width, 1);
  const xAxis2Domain =
    showChartType === TraceShowChartType.StrikeBars
      ? [xAxisStartDomain, xAxisEndDomain]
      : [0, 0.19];

  const lastCandlesChartData = useMemo<{
    chartData?: Plotly.PlotData[];
    annotations?: Plotly.PlotData[];
  }>(() => {
    const contourX = contourData?.chartData?.x ?? [];
    const priceLastCandle = getLastCandle();
    const chartData: Plotly.PlotData[] = [];
    const annotations: Plotly.PlotData[] = [];

    if (contourX.length === 0) {
      return { chartData, annotations };
    }

    const hiroYArr = deltaLine?.y ?? [];
    const hiroLastClose = hiroYArr[hiroYArr.length - 1];

    const x1 = contourX[0];
    const x2 = contourX[contourX.length - 1];
    const opacity = 0.6;

    if (priceLastCandle != null) {
      const close = Math.round(priceLastCandle.close);
      const color = hexToRGBA(heatmapColorSettings.lastCandleColor, opacity);

      chartData.push({
        x: [x1, x2],
        y: [close, close],
        xaxis: 'x',
        yaxis: 'y',
        mode: 'lines',
        line: {
          dash: 'dot',
          color,
          width: 0.5,
        },
        name: close,
        hoverinfo: 'y',
      });

      annotations.push({
        x: xAxisStartDomain - 0.01,
        xanchor: 'right',
        xref: 'paper',
        y: close,
        text: `${close}`,
        font: {
          size: 11,
          color: theme.palette.background.default,
        },
        showarrow: false,
        bgcolor: theme.palette.text.primary,
        opacity,
        borderpad: 2,
      });
    }

    if (hiroLastClose != null) {
      const color = hexToRGBA(heatmapColorSettings.hiroColor, opacity);

      chartData.push({
        x: [x1, x2],
        y: [hiroLastClose, hiroLastClose],
        xaxis: 'x',
        yaxis: 'y2',
        mode: 'lines',
        line: {
          dash: 'dot',
          color,
          width: isMobile ? 2 : 0.5, // needs to be thicker to be visible on mobile
        },
        name: hiroLastClose,
        hoverinfo: 'y',
      });

      annotations.push({
        x: xAxisEndDomain + 0.01,
        xanchor: 'left',
        xref: 'paper',
        y: hiroLastClose,
        yaxis: 'y2',
        yref: 'y2',
        text: `${formatAsCompactNumber(hiroLastClose)}`,
        font: {
          size: 11,
          color: `#fff`,
        },
        showarrow: false,
        bgcolor: heatmapColorSettings.hiroColor,
        borderpad: 2,
      });
    }

    return { chartData, annotations };
  }, [
    contourData,
    isMobile,
    heatmapColorSettings,
    deltaLine,
    getLastCandle,
    theme,
    xAxisEndDomain,
    xAxisStartDomain,
  ]);

  const { onHover, hoverInfo, setHoverInfo, crosshairsChartData } = useTooltip({
    selectedLense,
    intradaySym,
    intradayDate,
    heatmapColorSettings,
    strikeBarsTracker,
    strikeBarsData,
    statsDataMap,
    deltaCandles: deltaLine?.candles,
    priceCandles: priceCandleChartdata?.candles,
    hiroSym,
    minY,
    maxY,
    minX,
    maxX,
  });

  const getPlotlyData = () => {
    const heatmapData: Plotly.PlotData[] = [
      priceCandleChartdata,
      contourData?.chartData
        ? {
            ...contourData?.chartData,
            showscale: showColorScale,
            ...(isMobile && showColorScale
              ? {
                  colorbar: {
                    orientation: 'h',
                    y: -0.15,
                    x: 0.55,
                    xanchor: 'center',
                    yanchor: 'top',
                    len: 0.8,
                    thickness: 10,
                    title: {
                      text:
                        selectedGreek === TraceGreek.Delta
                          ? 'Delta ($)'
                          : 'Gamma ($)',
                      side: 'bottom',
                      font: { size: 10 },
                    },
                  },
                }
              : {}),
            line: {
              smoothing: 0.9,
              width: showContourLines ? undefined : 0,
              color: heatmapColorSettings.contourLineColor,
            },
          }
        : null,
      ...levelsChartData.chartData,
      deltaLine,
      ...(lastCandlesChartData.chartData ?? []),
      ...crosshairsChartData,
      priceActionLineData,
    ].filter((d) => d != null);

    let gexChartData: Plotly.PlotData[] = [];
    let trackerData: Plotly.PlotData[] = [];

    if (strikeBarType !== TraceStrikeBarType.NONE) {
      gexChartData =
        (strikeBarsData?.chartData?.length ?? 0) > 0 && !strikeBarsLoading
          ? strikeBarsData!.chartData
          : // needed for it to show loading even without data
            [{ xaxis: 'x2', type: 'bar', visible: false }];

      if (gexChartData && gexChartData[0].x && gexChartData[0].y) {
        trackerData = strikeBarsData?.trackerData ?? [];
      }
    }

    const strikeBarsDisplayData = [...trackerData, ...gexChartData].filter(
      (d) => d != null,
    );

    // Detect when all strike bar values are zero (e.g. 0DTE after market close).
    // Skip when history whiskers are enabled — the tracker still has data to show.
    const strikeBarsAllZero =
      strikeBarType !== TraceStrikeBarType.NONE &&
      !strikeBarsLoading &&
      !strikeBarsHistoryEnabled &&
      (strikeBarsData?.chartData?.length ?? 0) > 0 &&
      strikeBarsData!.chartData.every(
        (trace) =>
          trace.x == null ||
          (trace.x as number[]).every((v: number) => v === 0),
      );

    const noDataAnnotation: Partial<Plotly.Annotations> | null =
      strikeBarsAllZero && showChartType !== TraceShowChartType.Heatmap
        ? {
            xref: 'x2',
            yref: 'paper',
            x: 0,
            y: 0.5,
            text: showGexZeroDte
              ? 'No 0DTE data available<br>(market closed)'
              : 'No data available',
            showarrow: false,
            font: { size: 14, color: theme.palette.text.secondary },
            xanchor: 'center',
            yanchor: 'middle',
          }
        : null;

    let chartData;
    let annotations = levelsChartData.annotations;

    if (showChartType === TraceShowChartType.Both) {
      chartData = heatmapData.concat(strikeBarsDisplayData);
      annotations = annotations.concat([
        ...lastCandlesChartData.annotations,
        ...(strikeBarsData?.annotations ?? []),
      ]);
    } else if (showChartType === TraceShowChartType.Heatmap) {
      chartData = heatmapData;
      annotations = annotations.concat(lastCandlesChartData.annotations);
    } else {
      chartData = strikeBarsDisplayData;
      annotations = strikeBarsData?.annotations ?? [];
    }

    if (noDataAnnotation != null) {
      annotations = annotations.concat([noDataAnnotation]);
    }

    return { chartData, annotations };
  };

  // if we have zoomData below, the array is just 2 items, the min and max
  const watermarkXArr = zoomData?.x
    ? zoomData.x.map((str) => dayjs(str))
    : contourData?.chartData?.x;
  const watermarkYArr = zoomData?.y ?? contourData?.chartData?.y;
  const watermarkInverseSize = isMobile ? 3 : isTablet ? 5 : 10; // lower value = bigger logo
  // Suppress the watermark on mobile — it covers too much of a tiny chart.
  const suppressWatermark = isMobile;
  const watermark: Partial<Image> =
    suppressWatermark ||
    showChartType === TraceShowChartType.StrikeBars ||
    (watermarkXArr?.length ?? 0) === 0 ||
    (watermarkYArr?.length ?? 0) === 0
      ? {}
      : {
          x: watermarkXArr![0].valueOf(),
          y: watermarkYArr![0],
          // same as above, these two formulas below are the product of repeated ui testing
          sizex:
            (watermarkXArr![watermarkXArr!.length - 1].valueOf() -
              watermarkXArr![0].valueOf()) /
            watermarkInverseSize,
          sizey:
            (watermarkYArr![watermarkYArr!.length - 1] - watermarkYArr![0]) /
            watermarkInverseSize,
          source: 'images/spotgamma-logo-full.png',
          xanchor: 'left',
          xref: 'x',
          yanchor: 'bottom',
          yref: 'y',
          layer: 'above',
          opacity: 0.5,
        };

  const plotlyData = getPlotlyData();

  const hasError =
    (userTraceV2Enabled
      ? intradayTimestampsError ||
        intradayDeltaError ||
        intradayGammaError ||
        mmGammaError
      : contourDataError || gammaTableError) ||
    candlesApiError ||
    deltaLineError;

  const isPlotLoading =
    candlesApiPriceArrayBufferLoading ||
    deltaLineLoading ||
    (userTraceV2Enabled
      ? intradayDeltaLoading || intradayGammaLoading
      : countourDataLoading || gammaTableLoading) ||
    !contourData;

  const body = (
    <Loader
      isLoading={userTraceV2Enabled && intradayTimestampsLoading}
      message={
        (userTraceV2Enabled ? intradayTimestampsError : hasError)
          ? 'Error retrieving data. Automatically retrying...'
          : undefined
      }
    >
      <Center>
        <ExpandableContentWrapper
          isOpen={fullscreen}
          onToggle={(v) => setFullscreen(v)}
          styleOverrides={{ background: theme.palette.background.default }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              // Canvas: fill the tile so the inner flexbox has a height to distribute.
              ...(mode === 'canvas' ? { height: '100%', minHeight: 0 } : {}),
            }}
          >
            <Box
              sx={{
                backgroundColor: `${theme.palette.background.default} !important`,
                marginTop: isMobile ? 0 : '5px',
                // Canvas: flex column holding toolbar / plot / slider.
                ...(mode === 'canvas'
                  ? {
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                    }
                  : {}),
              }}
            >
              {/* Top chrome (toolbar + mobile view-mode buttons): auto-height flex child. */}
              <Box>
                <Box>
                  {!isDesktop ? (
                    <TraceCompactControls
                      timestamp={timestamp}
                      timestamps={timestamps}
                      zoomData={zoomData}
                      resetZoom={() => {
                        setZoomData(undefined);
                        resetStrikeBarsRange();
                      }}
                      setStrikeBarsLoading={setStrikeBarsLoading}
                      statsData={statsDataMap.get(
                        getDateFormatted(intradayDate),
                      )}
                      gammaAtLastPriceUninverted={gammaAtLastPriceUninverted}
                    />
                  ) : (
                    <TraceControls
                      timestamp={timestamp}
                      timestamps={timestamps}
                      zoomData={zoomData}
                      resetZoom={() => {
                        setZoomData(undefined);
                        resetStrikeBarsRange();
                      }}
                      setStrikeBarsLoading={setStrikeBarsLoading}
                      statsData={statsDataMap.get(
                        getDateFormatted(intradayDate),
                      )}
                      gammaAtLastPriceUninverted={gammaAtLastPriceUninverted}
                      lastUpdateCheckAt={dayjs()}
                    />
                  )}
                </Box>
                {isMobile && (
                  <Box
                    margin="auto"
                    width={1}
                    textAlign="center"
                    marginBottom="10px"
                  >
                    <ButtonGroup className="canvas-no-drag">
                      <Button
                        className="canvas-no-drag"
                        sx={{ fontSize: '13px', textTransform: 'none' }}
                        variant={
                          showChartType === TraceShowChartType.Heatmap
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={() =>
                          setShowChartType(TraceShowChartType.Heatmap)
                        }
                      >
                        Heatmap
                      </Button>
                      <Button
                        className="canvas-no-drag"
                        sx={{ fontSize: '13px', textTransform: 'none' }}
                        variant={
                          showChartType === TraceShowChartType.StrikeBars
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={() =>
                          setShowChartType(TraceShowChartType.StrikeBars)
                        }
                      >
                        Strike Bars
                      </Button>
                    </ButtonGroup>
                  </Box>
                )}
              </Box>

              <Box
                ref={plotAreaRef}
                sx={
                  mode === 'canvas'
                    ? {
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                        position: 'relative',
                      }
                    : undefined
                }
              >
                <Plot
                  className="canvas-no-drag"
                  onRelayout={onZoom}
                  data={plotlyData.chartData}
                  onHover={onHover}
                  onUnhover={() => {
                    setHoverInfo(null);
                  }}
                  onClick={isMobile ? onHover : undefined}
                  config={{ showAxisDragHandles: false }}
                  layout={{
                    width,
                    height,
                    images: [watermark],
                    margin: {
                      l: !isDesktop ? 25 : 20,
                      r: !isDesktop ? 30 : isMobile ? 40 : 20,
                      b: !isDesktop
                        ? showColorScale
                          ? 40
                          : 20
                        : isMobile
                          ? showColorScale
                            ? 55
                            : 30
                          : 50,
                      t: 0,
                      pad: !isDesktop ? 2 : 4,
                    },
                    paper_bgcolor: theme.palette.background.default,
                    plot_bgcolor: theme.palette.background.default,
                    font: {
                      color: theme.palette.text.primary,
                    },
                    xaxis:
                      showChartType === TraceShowChartType.StrikeBars
                        ? {}
                        : {
                            rangeslider: {
                              visible: false,
                            },
                            domain: [xAxisStartDomain, xAxisEndDomain],
                            fixedrange: false,
                            range: zoomData?.x,
                          },
                    xaxis2:
                      strikeBarType === TraceStrikeBarType.NONE ||
                      showChartType === TraceShowChartType.Heatmap
                        ? {}
                        : {
                            domain: xAxis2Domain,
                            ...(strikeBarsRange != null
                              ? { range: strikeBarsRange }
                              : { autorange: true }),
                            ...(strikeBarsLoading
                              ? {
                                  showline: false,
                                  zeroline: false,
                                  title: 'Updating. May take a minute...',
                                  showticklabels: false,
                                }
                              : {
                                  zeroline: showZeroLine,
                                  showline: false,
                                  title: {
                                    text: gexTitle(),
                                    font: {
                                      size: 12,
                                    },
                                  },
                                }),
                          },
                    yaxis: {
                      // you must specify a y range otherwise there will be unwanted padding with the
                      // tracker scatter trace on OI/NET_OI
                      range: getYRange(zoomData, minY, maxY, strikeBarType),
                      fixedrange: false,
                      title: shouldShowAxisLabels(width)
                        ? {
                            text: 'Strike / Price ($)',
                            standoff: width > 1_050 ? 15 : 5,
                          }
                        : isMobile
                          ? {
                              text: 'Strike ($)',
                              font: { size: 10 },
                              standoff: 2,
                            }
                          : undefined,
                    },
                    yaxis2:
                      deltaLine == null
                        ? { fixedrange: false }
                        : {
                            fixedrange: false,
                            overlaying: 'y',
                            side: 'right',
                            position:
                              xAxisEndDomain + (isMobile ? -0.01 : 0.005),
                            range:
                              zoomData?.y2 ??
                              getHiroRange(deltaLine, priceBounds),
                            title: shouldShowAxisLabels(width)
                              ? { text: 'HIRO' }
                              : isMobile
                                ? { text: 'HIRO', font: { size: 10 } }
                                : undefined,
                          },
                    showlegend: false,
                    annotations: plotlyData.annotations,
                    barmode: 'overlay',
                    modebar: {
                      // remove all plotly buttons except the download as image one
                      // they simply dont work well with our chart
                      remove: [
                        'autoScale2d',
                        'hoverCompareCartesian',
                        'hovercompare',
                        'lasso2d',
                        'orbitRotation',
                        'pan2d',
                        'pan3d',
                        'resetCameraDefault3d',
                        'resetCameraLastSave3d',
                        'resetGeo',
                        'resetScale2d',
                        'resetViewMapbox',
                        'resetViews',
                        'select2d',
                        'sendDataToCloud',
                        'tableRotation',
                        'toggleHover',
                        'toggleSpikelines',
                        'togglehover',
                        'togglespikelines',
                        'zoom2d',
                        'zoom3d',
                        'zoomIn2d',
                        'zoomInGeo',
                        'zoomInMapbox',
                        'zoomOut2d',
                        'zoomOutGeo',
                        'zoomOutMapbox',
                        // remove toImage if mobile. if not, add dup key because ts complains
                        isMobile ? 'toImage' : 'zoom2d',
                      ],
                    },
                  }}
                />
              </Box>
              {hoverInfo}
              {/* Bottom chrome (time slider) — auto-height flex child. */}
              <Box sx={{ width: `${width}px` }}>
                <TraceTimeSlider
                  date={intradayDate}
                  timestamps={timestamps}
                  chartWidth={width}
                  timestamp={timestamp}
                  setTimestamp={setTimestamp}
                  prefetch={prefetch}
                />
              </Box>
            </Box>
          </Box>
        </ExpandableContentWrapper>
      </Center>
    </Loader>
  );
  return (
    <Box
      sx={
        (userTraceV2Enabled ? intradayTimestampsLoading : isPlotLoading)
          ? { width: 1, height: isMobile ? 1 : `${height}px` }
          : {}
      }
    >
      {body}
    </Box>
  );
};
